import { httpsCallable } from 'firebase/functions';
import type {
  NotificationRecipientsUpsertRequest,
  NotificationRecipientsUpsertResponse,
} from '@kawai/shared';
import { functions } from '../firebase.js';

const callable = httpsCallable<
  NotificationRecipientsUpsertRequest,
  NotificationRecipientsUpsertResponse
>(functions, 'notificationRecipientsUpsert');

/**
 * N-15b: `notificationRecipientsUpsert` Callable の薄いラッパ（名簿の全量置換）。
 * 書込先 tenantId はサーバが Claims から決めるため入力に含めない。
 */
export async function callNotificationRecipientsUpsert(
  input: NotificationRecipientsUpsertRequest,
): Promise<NotificationRecipientsUpsertResponse> {
  const res = await callable(input);
  return res.data;
}

interface FunctionsErrorLike {
  code: string;
  message?: string;
}

function isFunctionsErrorLike(err: unknown): err is FunctionsErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string' &&
    (err as { code: string }).code.startsWith('functions/')
  );
}

/** Callable のエラーを運用者向け日本語メッセージに変換。 */
export function notificationRecipientsUpsertErrorMessage(err: unknown): string {
  if (isFunctionsErrorLike(err)) {
    switch (err.code) {
      case 'functions/permission-denied':
        return '通知先を保存する権限がありません（テナント管理者のみ / 閲覧専用セッションでは保存できません）。';
      case 'functions/unauthenticated':
        return 'セッションが切れました。再ログインしてください。';
      case 'functions/invalid-argument':
        return `入力値に誤りがあります：${err.message ?? ''}`;
      case 'functions/not-found':
        return `LINE 宛先に選んだ顧客が見つかりません：${err.message ?? ''}`;
      case 'functions/failed-precondition':
        return `保存できませんでした：${err.message ?? ''}`;
      default:
        return `通知先の保存に失敗しました（${err.code}）。`;
    }
  }
  if (err instanceof Error) return `通知先の保存に失敗しました：${err.message}`;
  return '通知先の保存に失敗しました。';
}
